import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  ArrowLeft, Activity, Clock, TrendingUp, AlertCircle, RefreshCw, Trophy, Share2, Copy, Settings
} from 'lucide-react';
import {
  monitorApi, statusApi, gamificationApi, formatUptime, formatResponseTime, formatDate, getAuthHeaders
} from '../utils/api';
import RelativeTime from './RelativeTime';
import { Monitor, PingLog, MonitorGamification, MonitorStatus } from '../types';
import StatusGraph from './StatusGraph';
import MonitorModal from './MonitorModal';
import EmailRecipientsInput from './EmailRecipientsInput';

const MonitorDetailsRouter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const monitorId = parseInt(id || '0');

  const [monitor, setMonitor] = useState<Monitor | null>(null);
  const [monitorStatus, setMonitorStatus] = useState<MonitorStatus | null>(null);
  const [pingHistory, setPingHistory] = useState<PingLog[]>([]);
  const [gamification, setGamification] = useState<MonitorGamification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<'24h' | '7d' | '30d' | 'all'>('24h');
  const [copied, setCopied] = useState(false);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [achievementsPage, setAchievementsPage] = useState(1);
  const [hasMoreAchievements, setHasMoreAchievements] = useState(true);
  const [achievementsPerPage] = useState(20);
  const [showEditModal, setShowEditModal] = useState(false);

  const [alertSettings, setAlertSettings] = useState({
    discord_webhook_url: monitor?.discord_webhook_url || '',
    alert_email: monitor?.alert_email || '',
    smtp_from: monitor?.smtp_from || '',
    use_global_alerts: Boolean(monitor?.use_global_alerts ?? true),
    alert_down_threshold: monitor?.alert_down_threshold || 1,
    alert_up_threshold: monitor?.alert_up_threshold || 1,
    alerts_enabled: monitor?.alerts_enabled !== null && monitor?.alerts_enabled !== undefined ? Boolean(monitor.alerts_enabled) : true,
    alert_frequency: monitor?.alert_frequency || 'always',
    alert_debounce: monitor?.alert_debounce || 1,
    discord_alerts_enabled: monitor?.discord_alerts_enabled !== null && monitor?.discord_alerts_enabled !== undefined ? Boolean(monitor.discord_alerts_enabled) : true,
    email_alerts_enabled: monitor?.email_alerts_enabled !== null && monitor?.email_alerts_enabled !== undefined ? Boolean(monitor.email_alerts_enabled) : true,
    discord_frequency: monitor?.discord_frequency || 'always',
    email_frequency: monitor?.email_frequency || 'always',
  });

  useEffect(() => {
    if (monitorId > 0) {
      loadMonitorDetails();
    }
  }, [monitorId, timeFilter]);

  useEffect(() => {
    if (monitor) {
      setAlertSettings({
        discord_webhook_url: monitor.discord_webhook_url || '',
        alert_email: monitor.alert_email || '',
        smtp_from: monitor.smtp_from || '',
        use_global_alerts: Boolean(monitor.use_global_alerts ?? true),
        alert_down_threshold: monitor.alert_down_threshold || 1,
        alert_up_threshold: monitor.alert_up_threshold || 1,
        alerts_enabled: monitor.alerts_enabled !== null && monitor.alerts_enabled !== undefined ? Boolean(monitor.alerts_enabled) : true,
        alert_frequency: monitor.alert_frequency || 'always',
        alert_debounce: monitor.alert_debounce || 1,
        discord_alerts_enabled: monitor.discord_alerts_enabled !== null && monitor.discord_alerts_enabled !== undefined ? Boolean(monitor.discord_alerts_enabled) : true,
        email_alerts_enabled: monitor.email_alerts_enabled !== null && monitor.email_alerts_enabled !== undefined ? Boolean(monitor.email_alerts_enabled) : true,
        // Support both frontend alias fields and backend-provided names
        discord_frequency: (monitor as any).discord_frequency || (monitor as any).discord_alert_frequency || 'always',
        email_frequency: (monitor as any).email_frequency || (monitor as any).email_alert_frequency || 'always',
      });
    }
  }, [monitor]);

  const loadMonitorDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const [monitorData, historyData, gamificationData] = await Promise.all([
        monitorApi.getMonitorStatus(monitorId),
        statusApi.getMonitorHistory(monitorId, 100, timeFilter === '24h' ? 24 : undefined, timeFilter === '7d' ? 7 : timeFilter === '30d' ? 30 : undefined),
        gamificationApi.getMonitorGamification(monitorId)
      ]);

      setMonitor(monitorData.monitor);
      setMonitorStatus(monitorData);
      setPingHistory(historyData);
      setGamification(gamificationData);
      
      // Load initial achievements
      await loadAchievements(1, true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load monitor details');
    } finally {
      setLoading(false);
    }
  };

  const loadAchievements = async (page: number, reset: boolean = false) => {
    if (achievementsLoading) return;
    
    try {
      setAchievementsLoading(true);
      
      const response = await gamificationApi.getMonitorAchievements(monitorId);
      const allAchievements = response.achievements || [];
      
      // Simulate pagination on the frontend since the API doesn't support it yet
      const startIndex = (page - 1) * achievementsPerPage;
      const endIndex = startIndex + achievementsPerPage;
      const pageAchievements = allAchievements.slice(startIndex, endIndex);
      
      if (reset) {
        setGamification(prev => prev ? {
          ...prev,
          achievements: pageAchievements
        } : null);
        setAchievementsPage(1);
      } else {
        setGamification(prev => prev ? {
          ...prev,
          achievements: [...(prev.achievements || []), ...pageAchievements]
        } : null);
        setAchievementsPage(page);
      }
      
      setHasMoreAchievements(endIndex < allAchievements.length);
    } catch (err) {
      console.error('Failed to load achievements:', err);
    } finally {
      setAchievementsLoading(false);
    }
  };

  const loadMoreAchievements = () => {
    if (!achievementsLoading && hasMoreAchievements) {
      loadAchievements(achievementsPage + 1);
    }
  };

  const copyShareLink = async () => {
    const shareUrl = `${window.location.origin}/public/monitor/${monitorId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleAlertSettingsUpdate = async () => {
    if (!monitor) return;
    
    try {
      // Only send fields that have values, convert empty strings to null
      const updateData: any = {};
      
      if (alertSettings.discord_webhook_url) {
        updateData.discord_webhook_url = alertSettings.discord_webhook_url;
      } else {
        updateData.discord_webhook_url = null;
      }
      
      if (alertSettings.alert_email) {
        updateData.alert_email = alertSettings.alert_email;
      } else {
        updateData.alert_email = null;
      }
      
      if (alertSettings.smtp_from) {
        updateData.smtp_from = alertSettings.smtp_from;
      } else {
        updateData.smtp_from = null;
      }
      
      updateData.use_global_alerts = Boolean(alertSettings.use_global_alerts);
      updateData.alert_down_threshold = alertSettings.alert_down_threshold;
      updateData.alert_up_threshold = alertSettings.alert_up_threshold;
      updateData.alerts_enabled = Boolean(alertSettings.alerts_enabled);
      // Unified frequency retained for backward compatibility
      updateData.alert_frequency = alertSettings.alert_frequency;
      // Per-channel frequency settings
      if ((alertSettings as any).discord_frequency) {
        updateData.discord_alert_frequency = (alertSettings as any).discord_frequency;
      }
      if ((alertSettings as any).email_frequency) {
        updateData.email_alert_frequency = (alertSettings as any).email_frequency;
      }
      updateData.alert_debounce = alertSettings.alert_debounce;
      
      await monitorApi.updateMonitor(monitor.id, updateData);
      
      // Don't reload - just update the local state
    } catch (err) {
      console.error('Failed to update alert settings:', err);
    }
  };

  const handleTestAlert = async () => {
    if (!monitor) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3501'}/api/alerts/test-monitor/${monitor.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({}),
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`Test alerts sent for ${monitor.name}!\nEmail: ${result.data.emailSent ? 'Sent' : 'Failed'}\nDiscord: ${result.data.discordSent ? 'Sent' : 'Failed'}`);
      } else {
        alert(`Failed to send test alerts: ${result.error}`);
      }
    } catch (err) {
      console.error('Failed to send test alert:', err);
      alert('Failed to send test alert');
    }
  };

  const handleEditMonitor = async (formData: any) => {
    try {
      await monitorApi.updateMonitor(monitorId, formData);
      await loadMonitorDetails(); // Refresh the monitor data
    } catch (error) {
      console.error('Failed to update monitor:', error);
      throw error;
    }
  };

  const calculateStats = () => {
    if (!pingHistory.length) return null;

    const totalPings = pingHistory.length;
    const successfulPings = pingHistory.filter(ping => ping.is_up).length;
    const failedPings = totalPings - successfulPings;
    const uptimePercentage = (successfulPings / totalPings) * 100;
    const averageResponseTime = pingHistory.reduce((sum, ping) => sum + ping.response_time, 0) / totalPings;
    const minResponseTime = Math.min(...pingHistory.map(ping => ping.response_time));
    const maxResponseTime = Math.max(...pingHistory.map(ping => ping.response_time));

    return {
      totalPings,
      successfulPings,
      failedPings,
      uptimePercentage,
      averageResponseTime,
      minResponseTime,
      maxResponseTime
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading monitor details...</p>
        </div>
      </div>
    );
  }

  if (error || !monitor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="alert alert-error">
          <AlertCircle className="w-5 h-5" />
          <span>{error || 'Monitor not found'}</span>
        </div>
        <button onClick={() => navigate('/dashboard')} className="btn btn-outline mt-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="btn btn-outline btn-sm">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div>
            <h1 className="text-3xl font-bold">{monitor.name}</h1>
            <p className="text-base-content/70">{monitor.url}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="btn btn-primary btn-sm"
          >
            <Settings className="w-4 h-4" />
            Edit Monitor
          </button>
          <button
            onClick={async () => {
              if (!monitor) return;
              const confirmed = confirm('Reset this monitor\'s statistics? This will delete ping history, streaks, and achievements. Settings will be preserved.');
              if (!confirmed) return;
              try {
                const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3501'}/api/monitors/${monitor.id}/reset`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                  body: JSON.stringify({})
                });
                const result = await response.json();
                if (response.ok && result.success) {
                  alert('Monitor statistics reset.');
                  await loadMonitorDetails();
                } else {
                  alert(result.error || 'Failed to reset monitor');
                }
              } catch (err) {
                console.error('Failed to reset monitor:', err);
                alert('Failed to reset monitor');
              }
            }}
            className="btn btn-outline btn-error btn-sm"
          >
            Reset Stats
          </button>
          <button
            onClick={copyShareLink}
            className="btn btn-outline btn-sm"
          >
            {copied ? <Copy className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button
            onClick={loadMonitorDetails}
            className="btn btn-outline btn-sm"
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Monitor Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-primary">
            <Activity className="w-8 h-8" />
          </div>
          <div className="stat-title">Uptime</div>
          <div className="stat-value text-primary">{formatUptime(monitorStatus?.uptime_percentage || 0)}</div>
          <div className="stat-desc">{stats?.successfulPings || 0} successful, {stats?.failedPings || 0} failed</div>
        </div>

        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-info">
            <Clock className="w-8 h-8" />
          </div>
          <div className="stat-title">Avg Response</div>
          <div className="stat-value text-info">{formatResponseTime(stats?.averageResponseTime || 0)}</div>
          <div className="stat-desc">Min: {formatResponseTime(stats?.minResponseTime || 0)}, Max: {formatResponseTime(stats?.maxResponseTime || 0)}</div>
        </div>

        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-success">
            <TrendingUp className="w-8 h-8" />
          </div>
          <div className="stat-title">Current Streak</div>
          <div className="stat-value text-success">{gamification?.streak?.current_streak || 0}</div>
          <div className="stat-desc">Best: {gamification?.streak?.best_streak || 0}</div>
        </div>

        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-warning">
            <Trophy className="w-8 h-8" />
          </div>
          <div className="stat-title">Achievements</div>
          <div className="stat-value text-warning">{gamification?.achievements?.length || 0}</div>
          <div className="stat-desc">Total achievements earned</div>
        </div>
      </div>

      {/* Time Filter */}
      <div className="flex items-center gap-4 mb-6">
        <span className="text-sm font-medium">Time Period:</span>
        <div className="join">
          {(['24h', '7d', '30d', 'all'] as const).map((period) => (
            <button
              key={period}
              onClick={() => setTimeFilter(period)}
              className={`join-item btn btn-sm ${timeFilter === period ? 'btn-primary' : 'btn-outline'}`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h3 className="card-title">Response Time Trend</h3>
            <StatusGraph pingHistory={pingHistory} />
          </div>
        </div>

        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h3 className="card-title">Recent Pings</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {pingHistory.slice(0, 20).map((ping) => (
                <div key={ping.id} className="flex items-center justify-between p-2 bg-base-200 rounded">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${ping.is_up ? 'bg-success' : 'bg-error'}`} />
                    <span className="text-sm">{formatDate(ping.created_at)}</span>
                  </div>
                  <div className="text-sm opacity-70">
                    {formatResponseTime(ping.response_time)} • {ping.status_code}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gamification Section */}
      {gamification && (
        <div className="card bg-base-100 shadow-lg mb-8">
          <div className="card-body">
            <h3 className="card-title">Achievements ({gamification.total_achievements || 0} total)</h3>
            <div className="text-sm opacity-70 mb-2">Showing {gamification.achievements?.length || 0} of {gamification.total_achievements || 0}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {gamification.achievements?.map((achievement) => (
                <div key={achievement.id} className="p-4 bg-base-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{achievement.icon}</div>
                    <div>
                      <div className="font-semibold">{achievement.title}</div>
                      <div className="text-sm opacity-70">{achievement.description}</div>
                      <div className="text-xs opacity-50"><RelativeTime dateString={achievement.achieved_at} /></div>
                    </div>
                  </div>
                </div>
              ))}
              
              {achievementsLoading && (
                <div className="col-span-full flex justify-center p-4">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                </div>
              )}
              
              {hasMoreAchievements && !achievementsLoading && (
                <div className="col-span-full flex justify-center p-4">
                  <button
                    onClick={loadMoreAchievements}
                    className="btn btn-outline btn-sm"
                  >
                    Load More Achievements
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Individual Alert Settings */}
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h3 className="card-title">Alert Settings</h3>
          
          <div className="space-y-6 mt-6">
            {/* Main Alert Toggle */}
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  checked={alertSettings.alerts_enabled}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    setAlertSettings({ ...alertSettings, alerts_enabled: newValue });
                    
                    // Auto-save the alerts_enabled setting immediately
                    try {
                      const updateData = {
                        alerts_enabled: newValue
                      };
                      await monitorApi.updateMonitor(monitor!.id, updateData);
                      // Don't reload - just update the local state
                    } catch (err) {
                      console.error('Failed to save alert enabled setting:', err);
                      // Revert the checkbox if save failed
                      setAlertSettings({ ...alertSettings, alerts_enabled: !newValue });
                    }
                  }}
                  className="checkbox checkbox-primary"
                />
                <span className="label-text font-medium">Enable Alerts for this Monitor</span>
              </label>
            </div>

            {alertSettings.alerts_enabled && (
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    checked={alertSettings.use_global_alerts}
                    onChange={(e) => setAlertSettings({ ...alertSettings, use_global_alerts: e.target.checked })}
                    className="checkbox checkbox-primary"
                  />
                  <span className="label-text">Use Global Alert Settings</span>
                </label>
              </div>
            )}

            {alertSettings.alerts_enabled && !alertSettings.use_global_alerts && (
              <>
                {/* Discord Alerts Section */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="divider m-0 flex-1">Discord Alerts</div>
                    <label className="label cursor-pointer justify-start gap-3 ml-4">
                      <input
                        type="checkbox"
                        checked={alertSettings.discord_alerts_enabled}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          setAlertSettings({ ...alertSettings, discord_alerts_enabled: newValue });
                          try {
                            await monitorApi.updateMonitor(monitor!.id, { discord_alerts_enabled: newValue });
                          } catch (err) {
                            console.error('Failed to save Discord alerts enabled setting:', err);
                            setAlertSettings({ ...alertSettings, discord_alerts_enabled: !newValue });
                          }
                        }}
                        className="checkbox checkbox-primary"
                      />
                      <span className="label-text">Enabled</span>
                    </label>
                  </div>

                  {alertSettings.discord_alerts_enabled && (
                    <>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">Discord Webhook URL</span>
                          <span className="label-text-alt">Individual Discord webhook for this monitor</span>
                        </label>
                        <input
                          type="url"
                          className="input input-bordered w-full"
                          value={alertSettings.discord_webhook_url || ''}
                          onChange={(e) => setAlertSettings({ ...alertSettings, discord_webhook_url: e.target.value })}
                          placeholder="https://discord.com/api/webhooks/..."
                        />
                      </div>

                      {/* Discord Frequency */}
                      <div className="space-y-3">
                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-3">
                            <input
                              type="radio"
                              name="discordFrequency"
                              value="always"
                              checked={alertSettings.discord_frequency === 'always'}
                              onChange={(e) => setAlertSettings({ ...alertSettings, discord_frequency: e.target.value as 'always' | 'on_change' })}
                              className="radio radio-primary"
                            />
                            <div className="label-text">
                              <div className="font-medium">Always</div>
                              <div className="text-sm opacity-70">Send Discord alerts for every ping that meets the threshold</div>
                            </div>
                          </label>
                        </div>
                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-3">
                            <input
                              type="radio"
                              name="discordFrequency"
                              value="on_change"
                              checked={alertSettings.discord_frequency === 'on_change'}
                              onChange={(e) => setAlertSettings({ ...alertSettings, discord_frequency: e.target.value as 'always' | 'on_change' })}
                              className="radio radio-primary"
                            />
                            <div className="label-text">
                              <div className="font-medium">On Change Only</div>
                              <div className="text-sm opacity-70">Send Discord alerts only when status changes</div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Email Alerts Section */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="divider m-0 flex-1">Email Alerts</div>
                    <label className="label cursor-pointer justify-start gap-3 ml-4">
                      <input
                        type="checkbox"
                        checked={alertSettings.email_alerts_enabled}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          setAlertSettings({ ...alertSettings, email_alerts_enabled: newValue });
                          try {
                            await monitorApi.updateMonitor(monitor!.id, { email_alerts_enabled: newValue });
                          } catch (err) {
                            console.error('Failed to save email alerts enabled setting:', err);
                            setAlertSettings({ ...alertSettings, email_alerts_enabled: !newValue });
                          }
                        }}
                        className="checkbox checkbox-primary"
                      />
                      <span className="label-text">Enabled</span>
                    </label>
                  </div>

                  {alertSettings.email_alerts_enabled && (
                    <>
                      <EmailRecipientsInput
                        value={alertSettings.alert_email || ''}
                        onChange={(value) => setAlertSettings({ ...alertSettings, alert_email: value })}
                        placeholder="alerts@example.com"
                        label="Alert Emails"
                        description="Email addresses to receive alerts (comma-separated or add individually)"
                      />

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">From Email</span>
                          <span className="label-text-alt">Custom "from" email address</span>
                        </label>
                        <input
                          type="email"
                          className="input input-bordered w-full"
                          value={alertSettings.smtp_from || ''}
                          onChange={(e) => setAlertSettings({ ...alertSettings, smtp_from: e.target.value })}
                          placeholder="support@yourdomain.com"
                        />
                      </div>

                      {/* Email Frequency */}
                      <div className="space-y-3">
                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-3">
                            <input
                              type="radio"
                              name="emailFrequency"
                              value="always"
                              checked={alertSettings.email_frequency === 'always'}
                              onChange={(e) => setAlertSettings({ ...alertSettings, email_frequency: e.target.value as 'always' | 'on_change' })}
                              className="radio radio-primary"
                            />
                            <div className="label-text">
                              <div className="font-medium">Always</div>
                              <div className="text-sm opacity-70">Send email alerts for every ping that meets the threshold</div>
                            </div>
                          </label>
                        </div>
                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-3">
                            <input
                              type="radio"
                              name="emailFrequency"
                              value="on_change"
                              checked={alertSettings.email_frequency === 'on_change'}
                              onChange={(e) => setAlertSettings({ ...alertSettings, email_frequency: e.target.value as 'always' | 'on_change' })}
                              className="radio radio-primary"
                            />
                            <div className="label-text">
                              <div className="font-medium">On Change Only</div>
                              <div className="text-sm opacity-70">Send email alerts only when status changes</div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Alert Thresholds */}
                <div className="space-y-6">
                  <div className="divider">Alert Thresholds</div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">Down Threshold</span>
                        <span className="label-text-alt">Number of failures before alert</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className="input input-bordered w-full"
                        value={alertSettings.alert_down_threshold || 1}
                        onChange={(e) => setAlertSettings({ ...alertSettings, alert_down_threshold: parseInt(e.target.value) })}
                      />
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">Up Threshold</span>
                        <span className="label-text-alt">Number of successes before recovery alert</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className="input input-bordered w-full"
                        value={alertSettings.alert_up_threshold || 1}
                        onChange={(e) => setAlertSettings({ ...alertSettings, alert_up_threshold: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                </div>

                {/* Global Debounce */}
                {(alertSettings.discord_frequency === 'always' || alertSettings.email_frequency === 'always') && (
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">Alert Debounce (minutes)</span>
                        <span className="label-text-alt">Minimum time between alerts to prevent spam</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        className="input input-bordered w-full"
                        value={alertSettings.alert_debounce || 5}
                        onChange={(e) => setAlertSettings({ ...alertSettings, alert_debounce: parseInt(e.target.value) || 1 })}
                        placeholder="5"
                      />
                    </div>
                  )}

                <div className="flex gap-2">
                  <button
                    onClick={handleAlertSettingsUpdate}
                    className="btn btn-primary btn-sm"
                  >
                    Save Settings
                  </button>
                  <button
                    onClick={handleTestAlert}
                    className="btn btn-outline btn-sm"
                  >
                    Test Alerts
                  </button>
                </div>
              </>
            )}

            {/* Always show save button for other settings */}
            {alertSettings.alerts_enabled && alertSettings.use_global_alerts && (
              <div className="flex gap-2">
                <button
                  onClick={handleAlertSettingsUpdate}
                  className="btn btn-primary btn-sm"
                >
                  Save Settings
                </button>
                <button
                  onClick={handleTestAlert}
                  className="btn btn-outline btn-sm"
                >
                  Test Alerts
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Monitor Modal */}
      <MonitorModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditMonitor}
        monitor={monitor}
        title="Edit Monitor"
      />
    </div>
  );
};

export default MonitorDetailsRouter; 